// react
import { useCallback, useMemo, useState } from 'react';

// components
import {
  Avatar,
  Button,
  Dialog,
  Loader,
  LoadingTransition,
} from '@/components/atoms';
import {
  InviteClientForm,
  InviteEmailPreview,
  OnboardingComponent,
  RowAction,
} from '@/components/organisms';
import {
  OnboardingActionType,
  OnboardingFlow,
  PortalClientSummary,
  TablePage,
} from '@/components/pages';

// models
import {
  Account,
  AccountSummary,
  CareProfileFormEvent,
  CareRecipientSummary,
  Organization,
  ReferClientRequest,
  ReferralFormType,
} from '@karehero/models';

// tanstack
import { createColumnHelper } from '@tanstack/react-table';

// columns
const columnHelper = createColumnHelper<AccountSummary>();

const generateDefaultPersonalizedMessage = (
  clientName: string,
  partnerName: string,
) => {
  return `Hi ${clientName}, I'm sending over this invite as I think that you'll really benefit from KareHero. Thanks, ${partnerName}.`;
};

/**
 * ClientPortal shows the accounts and invited clients of an organization
 */
export const ClientPortal = ({
  isLoading,
  organization,
  ownerAccount,
  accounts,
  emailTemplate,
  inviteClient,
  resendInvite,
  notifyToast,
  isBackButton = true,
  getCareRecipientShortlistData,
  isMobile,
  isShortlistSummary,
  onPrintPdf,
}: ClientPortalProps) => {
  // state
  const [isValidationValid, setIsValidationValid] = useState(false);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isProfilePreviewOpen, setIsProfilePreviewOpen] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState<
    CareRecipientSummary[]
  >([]);
  const [formData, setFormData] = useState<ReferClientRequest>({
    referral: {},
    referrer: {},
  });
  const [cachedShortlistData, setCachedShortlistData] = useState<
    Record<string, CareRecipientSummary[]>
  >({});

  // validators
  const isGdprValid = (c: any): boolean => {
    return JSON.parse(c?.gdprSelections || '[]').length === 2;
  };

  // memos
  const defaultPersonalizedMessage = useMemo(() => {
    if (!ownerAccount || !formData.referral?.firstName) return undefined;

    return generateDefaultPersonalizedMessage(
      formData.referral.firstName,
      `${ownerAccount.firstName}`,
    );
  }, [formData, ownerAccount]);

  const isFormValid = useMemo(() => {
    return (
      isGdprValid(formData.referral) &&
      [
        'firstName',
        'lastName',
        'email',
        'phone',
        'personReceivingCare',
        'careSituationContext',
        'feePaid',
      ].every((x) => {
        const v = Object(formData.referral)?.[x];
        return v && v.length > 0;
      })
    );
  }, [formData.referral, organization]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('imgSrc', {
        header: '',
        cell: (info) => {
          const row = info.cell.row.original;
          let fallback =
            `${row.firstName?.[0] || ''}${row.lastName?.[0] || ''}`.toUpperCase();
          if (fallback.length === 0) {
            if (row.email && row.email.length > 0) {
              fallback = row.email[0]?.toUpperCase();
            } else {
              row.inviteKey?.[0]?.toUpperCase() || '';
            }
          }
          return <Avatar size='xs' fallback={fallback} />;
        },
      }),
      columnHelper.accessor('firstName', {
        header: 'First Name',
      }),
      columnHelper.accessor('lastName', {
        header: 'Last Name',
      }),
      columnHelper.accessor('email', {
        header: 'Email Address',
      }),
      columnHelper.accessor(
        (row) => {
          return row.careProfileFormEvents
            ?.filter((ev) => ev.eventType === 'form-submitted')
            ?.find(
              (sub: CareProfileFormEvent) =>
                sub.careProfileCategoryId === 'care-assessment',
            )?.submittedAt;
        },
        {
          id: 'careAssessment',
          header: 'Care Assessment',
          cell: (info) => {
            const date = info.getValue();
            return date
              ? `Completed ${new Date(date).toLocaleDateString('en-GB')}`
              : 'Incomplete';
          },
        },
      ),
      columnHelper.accessor(
        (row) => {
          return row.careProfileFormEvents
            ?.filter((ev) => ev.eventType === 'form-submitted')
            ?.find(
              (sub: CareProfileFormEvent) =>
                sub.careProfileCategoryId === 'financial-status',
            )?.submittedAt;
        },
        {
          id: 'financialAssessment',
          header: 'Financial Assessment',
          cell: (info) => {
            const date = info.getValue();
            return date
              ? `Completed ${new Date(date).toLocaleDateString('en-GB')}`
              : 'Incomplete';
          },
        },
      ),
      columnHelper.accessor('isRegistered', {
        header: 'Registered',
        cell: (info) => {
          return info.getValue() ? 'Yes' : 'No';
        },
      }),
    ],
    [organization],
  );

  const actions = useMemo<RowAction[]>(() => {
    return [
      {
        label: 'View',
        isHidden: (account: AccountSummary): boolean => {
          return (
            // Hide the view button unless the launch darkly flag is on, the account is onboarded and they have consented
            !isShortlistSummary || !account.isOnboarded || !account.isConsent
          );
        },
        onPress: async (selectedAccount: Account) => {
          // check if we've already cached the data
          if (cachedShortlistData[selectedAccount.id]) {
            setSelectedRecipient(cachedShortlistData[selectedAccount.id]);
            setIsProfilePreviewOpen(true);
            return;
          }
          const data = await getCareRecipientShortlistData(selectedAccount.id);
          setCachedShortlistData((prev) => ({
            ...prev,
            [selectedAccount.id]: data,
          }));
          setSelectedRecipient(data);
          setIsProfilePreviewOpen(true);
        },
      },
      {
        label: 'Reinvite',
        isHidden: (account: AccountSummary) =>
          !!account.isRegistered || (account.email?.length || 0) === 0,
        onPress: (account: AccountSummary) => {
          if (account.id) {
            resendInvite(account.id);

            notifyToast({
              title: 'Invitation Success',
              description: `Resent invitation to ${account.email || 'the user'}`,
            });
          }
        },
      },
    ];
  }, [resendInvite, notifyToast]);

  const inviteFormConfig = useMemo(
    () => [
      {
        id: 'invite',
        Component: OnboardingComponent,
        config: [
          {
            id: 'invite-client-form',
            component: (
              <InviteClientForm
                value={formData}
                formType={
                  organization?.organizationTypeId === 'ifa'
                    ? ReferralFormType.IFA
                    : ReferralFormType.SjpIFA
                }
                onChange={(newData: ReferClientRequest) =>
                  setFormData((prev) => ({ ...prev, ...newData }))
                }
                setIsValid={setIsValidationValid}
              />
            ),
            actions: [
              {
                label: 'Preview Email',
                onClick: OnboardingActionType.Next,
                iconRight: 'eye',
                isDisabled: !isFormValid || !isValidationValid,
              },
            ],
          },
          {
            id: 'invite-email-preview',
            component: (
              <InviteEmailPreview
                emailTemplate={emailTemplate}
                defaultPersonalizedMessage={defaultPersonalizedMessage}
                personalizedMessage={formData.referral.personalizedMsg}
                setPersonalizedMessage={(personalizedMsg: string) =>
                  setFormData((prev) => ({
                    ...prev,
                    referral: { ...prev.referral, personalizedMsg },
                  }))
                }
                clientName={formData.referral?.firstName || 'Hi'}
                inviterName={`${ownerAccount?.firstName} ${ownerAccount?.lastName}`}
              />
            ),
            actions: [
              {
                label: 'Back',
                onClick: OnboardingActionType.Back,
              },
              {
                label: 'Send Invite',
                onClick: OnboardingActionType.Next,
                iconRight: 'location-arrow',
              },
            ],
          },
        ],
      },
    ],
    [
      formData,
      isFormValid,
      defaultPersonalizedMessage,
      ownerAccount,
      emailTemplate,
      setFormData,
      isValidationValid,
    ],
  );

  // methods
  const handleInvite = useCallback(async () => {
    inviteClient(formData);
    setIsInviteDialogOpen(false);
    notifyToast({
      title: 'Invite Sent',
      description: 'Your client will receive an invite shortly in their email',
    });
  }, [formData, setIsInviteDialogOpen, inviteClient, notifyToast]);

  return (
    <LoadingTransition
      isLoading={isLoading}
      loading={<Loader />}
      content={
        <>
          <TablePage
            title={`${organization?.name} Invite Portal`}
            columns={columns}
            rows={accounts}
            actions={actions}
            isBackButton={isBackButton}
            toolbarActions={
              <Button
                ariaLabel='invite new clients'
                size='sm'
                onPress={() => setIsInviteDialogOpen(true)}
                iconLeft={'plus'}
                isRectangle
              >
                Invite client
              </Button>
            }
          />
          <Dialog
            title='Invite Client to KareHero'
            isCloseButton
            isOpen={isInviteDialogOpen}
            setIsOpen={(isOpen: boolean) => setIsInviteDialogOpen(isOpen)}
            variant='dialog'
            isNoPadding={true}
          >
            <OnboardingFlow
              config={inviteFormConfig}
              alignActions='right'
              onComplete={() => {
                handleInvite();
              }}
            />
          </Dialog>
          <Dialog
            variant='sheet'
            isOpen={isProfilePreviewOpen}
            setIsOpen={(isOpen: boolean) => setIsProfilePreviewOpen(isOpen)}
          >
            {selectedRecipient.length > 0 ? (
              selectedRecipient.map((recipient) => (
                <PortalClientSummary
                  key={recipient.id}
                  recipient={recipient}
                  isMobile={isMobile}
                  onPrintPdf={onPrintPdf}
                />
              ))
            ) : (
              <PortalClientSummary
                recipient={{} as CareRecipientSummary}
                isMobile={isMobile}
                onPrintPdf={onPrintPdf}
              />
            )}
          </Dialog>
        </>
      }
    />
  );
};

interface ClientPortalProps {
  isLoading?: boolean;
  organization?: Organization;
  ownerAccount?: Account;
  accounts?: Account[];
  emailTemplate: string;
  inviteClient: (req: ReferClientRequest) => void;
  resendInvite: (id: string) => void;
  notifyToast: (req: { title: string; description: string }) => void;
  isBackButton?: boolean;
  isMobile?: boolean;
  isShortlistSummary?: boolean;
  getCareRecipientShortlistData: (
    id: string,
  ) => Promise<CareRecipientSummary[]>;
  onPrintPdf: () => void;
}

ClientPortal.displayName = 'ClientPortal';

export default ClientPortal;
